/**
 * Shared form-builder constants — single source of truth.
 *
 * Previously duplicated between FormBuilder.jsx and FieldInspector.jsx.
 * Import from here instead of redefining locally.
 */

export const FIELD_TYPES = [
    { value: 'text',            label: 'Short Text',      icon: '📝' },
    { value: 'textarea',        label: 'Long Text',       icon: '📄' },
    { value: 'email',            label: 'Email',           icon: '📧' },
    { value: 'phone',            label: 'Phone',           icon: '📞' },
    { value: 'number',           label: 'Number',          icon: '🔢' },
    { value: 'date',             label: 'Date',            icon: '📅' },
    { value: 'url',              label: 'URL',             icon: '🔗' },
    { value: 'select',           label: 'Dropdown',        icon: '📋' },
    { value: 'multi_select',     label: 'Multi Select',    icon: '✅' },
    { value: 'checkbox',         label: 'Checkbox',        icon: '☑️' },
    { value: 'section_heading',  label: 'Section Heading', icon: '🔖' },
    { value: 'divider',          label: 'Divider',         icon: '➖' },
    { value: 'spacer',           label: 'Spacer',          icon: '⬜' },
    { value: 'html_block',       label: 'HTML Block',      icon: '📦' },
    { value: 'container',        label: 'Container',       icon: '🔲' },
];

export const STRUCTURAL_TYPES = ['section_heading', 'divider', 'spacer', 'html_block', 'container'];

export const TYPE_ICONS = Object.fromEntries(FIELD_TYPES.map(t => [t.value, t.icon]));

export const CONTACT_MAPPINGS = [
    { value: '', label: 'No mapping' },
    { group: 'Contact', value: 'first_name',         label: 'First Name' },
    { group: 'Contact', value: 'last_name',          label: 'Last Name' },
    { group: 'Contact', value: 'email',              label: 'Email' },
    { group: 'Contact', value: 'phone',              label: 'Phone' },
    { group: 'Contact', value: 'mobile',             label: 'Mobile' },
    { group: 'Contact', value: 'title',              label: 'Job Title' },
    { group: 'Contact', value: 'department',         label: 'Department' },
    { group: 'Contact', value: 'seniority',          label: 'Seniority' },
    { group: 'Contact', value: 'linkedin_url',       label: 'LinkedIn URL' },
    { group: 'Company', value: 'company_name',       label: 'Company Name' },
    { group: 'Company', value: 'company_website',    label: 'Company Website' },
    { group: 'Company', value: 'industry',            label: 'Industry' },
    { group: 'Company', value: 'company_size',        label: 'Company Size' },
    { group: 'Company', value: 'company_phone',       label: 'Company Phone' },
    { group: 'Company', value: 'company_email',       label: 'Company Email' },
    { group: 'Company', value: 'company_city',        label: 'Company City' },
    { group: 'Company', value: 'company_country',     label: 'Company Country' },
    { group: 'Company', value: 'company_linkedin_url', label: 'Company LinkedIn URL' },
];

// GHL v2 standard contact fields (mirror of gohighlevel.py GHL_STANDARD_FIELDS)
export const GHL_STANDARD_FIELDS = [
    { value: 'firstName', label: 'First Name' }, { value: 'lastName', label: 'Last Name' },
    { value: 'name', label: 'Full Name' }, { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' }, { value: 'companyName', label: 'Company Name' },
    { value: 'address1', label: 'Address' }, { value: 'city', label: 'City' },
    { value: 'state', label: 'State' }, { value: 'postalCode', label: 'Postal Code' },
    { value: 'country', label: 'Country' }, { value: 'website', label: 'Website' },
    { value: 'timezone', label: 'Timezone' }, { value: 'source', label: 'Source' },
    { value: 'dateOfBirth', label: 'Date of Birth' },
];

/**
 * Four-side margin → inline style object.
 * `margin` is `{ top, right, bottom, left }` in px; any side may be
 * absent. Returns undefined when no side is set so callers can skip the
 * style prop entirely. Shared by per-field margins (via marginStyle)
 * and the stepper header margin (via spacingStyle directly).
 */
export function spacingStyle(margin) {
    if (!margin) return undefined;
    const style = {};
    if (margin.top != null && margin.top !== '') style.marginTop = `${margin.top}px`;
    if (margin.right != null && margin.right !== '') style.marginRight = `${margin.right}px`;
    if (margin.bottom != null && margin.bottom !== '') style.marginBottom = `${margin.bottom}px`;
    if (margin.left != null && margin.left !== '') style.marginLeft = `${margin.left}px`;
    return Object.keys(style).length ? style : undefined;
}

/**
 * Per-element margin overrides → inline style object.
 * `node.margin` is `{ top, right, bottom, left }` in px; any side may be
 * absent. Returns undefined when no side is set so callers can skip the
 * style prop entirely. Spacers don't carry margins (they ARE spacing).
 * Delegates to spacingStyle() so the stepper header margin shares the
 * same renderer.
 */
export function marginStyle(node) {
    if (node?.field_type === 'spacer') return undefined;
    return spacingStyle(node?.margin);
}

/**
 * Form-level padding → inline style object.
 * `padding` is `{ top, right, bottom, left }` in px; any side may be
 * absent. Returns undefined when no side is set so callers can skip the
 * style prop entirely.
 */
export function paddingStyle(padding) {
    if (!padding) return undefined;
    const style = {};
    if (padding.top != null && padding.top !== '' && padding.top !== 0) style.paddingTop = `${padding.top}px`;
    if (padding.right != null && padding.right !== '' && padding.right !== 0) style.paddingRight = `${padding.right}px`;
    if (padding.bottom != null && padding.bottom !== '' && padding.bottom !== 0) style.paddingBottom = `${padding.bottom}px`;
    if (padding.left != null && padding.left !== '' && padding.left !== 0) style.paddingLeft = `${padding.left}px`;
    return Object.keys(style).length ? style : undefined;
}

export const INPUT = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20';

export const LABEL = 'block text-xs font-medium text-stone-600 mb-1';
